import { ProductCategory, ProductPaymentMethod, ProductSubscriptionViewModel } from '__generated__/kycTypes';
import { pascalToTitleCase } from '../../../utils/stringHelpers';
import { hasValue } from '../../../components/inputs/inputHelpers';
import { remoteConfigData } from '../../../config';
import { productLabel } from '../../../constants';
import { Option } from 'components/inputs/option';

export const partnerDetailsCacheString = 'partners-details';
export const productSubscriptionQueryName = 'productSubscriptions';
export const productPriceBandsQueryName = 'productPriceBands';

export const paymentMethodDisplay = (paymentMethod: string) => {
  return paymentMethod === ProductPaymentMethod.IoTecWallet ? 'ioTec Wallet' : pascalToTitleCase(paymentMethod);
};

const desiredOrder = [
  ProductCategory.CustomerOnboarding,
  ProductCategory.IdVerification,
  ProductCategory.FaceVerification,
  ProductCategory.FaceMatch,
  ProductCategory.PhoneVerification,
  ProductCategory.AmlScreening,
  ProductCategory.BusinessSearch,
  ProductCategory.DataConsent
];

interface ProductConfig {
  enabled: boolean;
  displayName: string;
}

type ProductConfigMap = Record<ProductCategory, ProductConfig>;

const productConfig = (): ProductConfigMap => {
  const dataConsentEnabled = remoteConfigData.enableUssdConsent();
  const customerOnboardingEnabled = remoteConfigData.enableCustomerOnboarding();

  return {
    [ProductCategory.IdVerification]: { enabled: true, displayName: productLabel(ProductCategory.IdVerification) },
    [ProductCategory.LandVerification]: { enabled: false, displayName: productLabel(ProductCategory.LandVerification) },
    [ProductCategory.TinVerification]: { enabled: false, displayName: productLabel(ProductCategory.TinVerification) },
    [ProductCategory.PhoneVerification]: { enabled: true, displayName: productLabel(ProductCategory.PhoneVerification) },
    [ProductCategory.DataConsent]: { enabled: dataConsentEnabled, displayName: productLabel(ProductCategory.DataConsent) },
    [ProductCategory.AmlScreening]: { enabled: true, displayName: productLabel(ProductCategory.AmlScreening) },
    [ProductCategory.BusinessSearch]: { enabled: true, displayName: productLabel(ProductCategory.BusinessSearch) },
    [ProductCategory.PhoneNumberActivity]: { enabled: false, displayName: productLabel(ProductCategory.PhoneNumberActivity) },
    [ProductCategory.CustomerOnboarding]: { enabled: customerOnboardingEnabled, displayName: productLabel(ProductCategory.CustomerOnboarding) },
    [ProductCategory.FaceVerification]: { enabled: true, displayName: productLabel(ProductCategory.FaceVerification) },
    [ProductCategory.FaceMatch]: { enabled: true, displayName: productLabel(ProductCategory.FaceMatch) },
  };
};

export const productDisplayName = (productCategory?: ProductCategory) : string => {
  if (hasValue(productCategory)) {
    return productConfig()[productCategory]?.displayName || '';
  } else {
    return '';
  }
};

export const enabledProductOptions = (): Option[] => {
  const config = productConfig();

  return (Object.entries(config) as [ProductCategory, ProductConfig][])
    .filter(([, value]) => value.enabled) 
    .map(([key, value]) => ({
      id: key,              
      name: value.displayName 
    }));
};

export const getOrderedEnabledSubscriptions = (subscriptions: ProductSubscriptionViewModel[]) : ProductSubscriptionViewModel[] => {
  return subscriptions.sort((a, b) => {
    return desiredOrder.indexOf(a.category) - desiredOrder.indexOf(b.category);
  }).filter((it) => productConfig()[it.category].enabled);
};

export const getOrderedEnabledProductNames = () => {
  return desiredOrder.filter((it) => productConfig()[it].enabled).map((product) => productDisplayName(product));
};

export const getOrderedEnabledProductCategories = () => {
  return desiredOrder.filter((it) => productConfig()[it].enabled)
    .map((product) => {
      return {
        id: product,
        name: productDisplayName(product),
      };
    });
};
